import { DialogTitle } from '@headlessui/react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@/components/common/modal/modal';
import { useIsDesktop } from '@/hooks/use-user-platform';
import type { IncomingDialogPrompt, Prompt } from '@/stores/prompter';
import cn from '@/utils/merge-class-names';

interface Props {
  prompt: Prompt & IncomingDialogPrompt;
}

export default function DialogPrompt({ prompt }: Props) {
  const { t } = useTranslation();
  const confirmBtnRef = useRef<HTMLButtonElement | null>(null);
  const isDesktop = useIsDesktop();

  const availableButtonCount = prompt?.resolutionButtons?.filter((button) => !button.hidden)?.length ?? 0;

  useEffect(() => {
    if (confirmBtnRef && isDesktop) {
      confirmBtnRef.current?.focus();
    }
  }, []);

  return (
    <Modal
      key={prompt.key}
      defaultOpen
      clickOutsideToClose={prompt.type !== 'alert'}
      onClose={() => prompt.resolvers.resolve(false)}>
      <DialogTitle className='my-4 text-base font-bold'>{prompt.title}</DialogTitle>

      {typeof prompt.content === 'string' ? <p className='text-secondary'>{prompt.content}</p> : prompt.content}

      <div
        className={cn('mb-4 mt-6 flex xs:items-center items-end justify-end gap-2', {
          'xs:flex-row flex-wrap': availableButtonCount > 1,
        })}>
        {prompt.resolutionButtons
          ?.filter((button) => !button.hidden)
          .map((button, index) => (
            <button
              key={`${button.resolution}-${index}`}
              type='button'
              disabled={prompt.isLoading}
              className={cn('btn h-10 min-h-10 btn-primary mode-embedded:rounded-full w-[8.25rem] font-normal', {
                'btn-error': button.type === 'destructive',
                'btn-ghost pointer-events-none': button.type === 'informative',
              })}
              onClick={() => prompt.resolvers.resolve(button.resolution)}>
              {prompt.isLoading ? <span className='loading loading-spinner loading-sm' /> : button.label}
            </button>
          )) ?? (
          <button
            ref={confirmBtnRef}
            data-testid='prompter-confirm-btn'
            type='button'
            disabled={prompt.isLoading}
            className='btn h-10 min-h-10 font-normal btn-primary mode-embedded:rounded-full'
            onClick={() => prompt.resolvers.resolve(true)}>
            {prompt.isLoading ? (
              <span className='loading loading-spinner loading-sm' />
            ) : (
              (prompt.resolveButtonLabel ?? t('common.confirm'))
            )}
          </button>
        )}

        {prompt.type !== 'alert' && (
          <button
            type='button'
            disabled={prompt.isLoading}
            className={cn('btn h-10 min-h-10 mode-embedded:rounded-full font-normal btn-ghost order-first', {
              'order-last': availableButtonCount > 1,
            })}
            onClick={() => prompt.resolvers.resolve(false)}>
            {prompt.rejectButtonLabel ?? t('common.cancel')}
          </button>
        )}
      </div>
    </Modal>
  );
}
